import { useRef, useState, useEffect } from "react";
import { useRouter } from "next/router";
import SpecialText from "../fragments/SpecialText";
import Thumbnail from "../fragments/Thumbnail";
import ThumbnailQuote from "../fragments/ThumbnailQuote";
import { useReveal } from '../hooks/UseReveal';

export default function News({ block }) {
	const router = useRouter();
	const revealRef = useReveal({ once: true });
	
	const [selectedLocation, setSelectedLocation] = useState('all');
	const [selectedCategory, setSelectedCategory] = useState('all');
	const [filteredPosts, setFilteredPosts] = useState([]);

	const normalizeSlug = (value) => {
		if (!value || typeof value !== 'string') return '';
		return value
			.toLowerCase()
			.trim()
			.replace(/&/g, 'and')
			.replace(/[^a-z0-9]+/g, '-')
			.replace(/(^-|-$)/g, '');
	};

	const getTermSlug = (term) => {
		if (!term) return '';
		if (term.slug) return normalizeSlug(term.slug);
		if (term.text) return normalizeSlug(term.text);
		return '';
	};

	// Initialize filters from URL query params
	useEffect(() => {
		const { location, category } = router.query;
		const locationValue = Array.isArray(location) ? location[0] : location;
		const categoryValue = Array.isArray(category) ? category[0] : category;
		if (locationValue) setSelectedLocation(locationValue);
		if (categoryValue) setSelectedCategory(categoryValue);
	}, [router.query]);

	// Filter posts based on selected filters
	useEffect(() => {
		if (!block || !block.posts) return;

		let filtered = block.posts;

		if (selectedLocation !== 'all') {
			const selectedLocationSlug = normalizeSlug(selectedLocation);
			filtered = filtered.filter(post =>
				post.location && post.location.some(loc => getTermSlug(loc) === selectedLocationSlug)
			);
		}

		if (selectedCategory !== 'all') {
			const selectedCategorySlug = normalizeSlug(selectedCategory);
			filtered = filtered.filter(post =>
				post.category && post.category.some(cat => getTermSlug(cat) === selectedCategorySlug)
			);
		}

		setFilteredPosts(filtered);
	}, [selectedLocation, selectedCategory, block]);

	const handleFilterChange = (type, value) => {
		// Update state immediately for smooth filtering
		if (type === 'location') {
			setSelectedLocation(prev => (prev === value ? 'all' : value));
		} else if (type === 'category') {
			setSelectedCategory(prev => (prev === value ? 'all' : value));
		} else if (type === 'all') {
			// Reset both filters
			setSelectedLocation('all');
			setSelectedCategory('all');
		}

		// Update URL without navigation
		const query = {};
		const nextLocation = type === 'location'
			? (selectedLocation === value ? 'all' : value)
			: selectedLocation;
		const nextCategory = type === 'category'
			? (selectedCategory === value ? 'all' : value)
			: selectedCategory;

		if (type !== 'all') {
			if (nextLocation !== 'all') query.location = nextLocation;
			if (nextCategory !== 'all') query.category = nextCategory;
		}

		const url = new URL(window.location.href);
		url.search = new URLSearchParams(query).toString();
		window.history.replaceState({}, '', url.toString());
	};

	// Function to render posts with quotes inserted at intervals
	const renderPostsWithQuotes = () => {
		if (filteredPosts.length === 0) {
			return (
				<div className="c-news-grid__cell c-news-grid__no-results">
					<p className="c-news-grid__no-results__text">No articles found matching the selected filters.</p>
				</div>
			);
		}

		const items = [];
		const quotes = block.quotes || [];
		
		filteredPosts.forEach((post, index) => {

			// Insert quote at the start
			if (index === 0 && quotes.length > 0) {
				const quote = quotes[0];
				
				items.push(
					<div className="c-news-grid__cell c-news-grid__cell--quote" key={`quote-${index}`}>
						<ThumbnailQuote quote={quote} />
					</div>
				);
			}

			// Add the post thumbnail
			items.push(
				<div className="c-news-grid__cell" key={`post-${post.permalink}-${index}`}>
					<Thumbnail post={post} limitHeight={false} newsPage={true} />
				</div>
			);

		});

		return items;
	};

	if (!block || !block.posts || block.posts.length === 0) return null;

	return (
		<div id={block.section_id} className="c-page__block c-page__block--news">
			<div className="c-news">
				<div className="l-grid">
					<div className="l-grid__col c-transition-in" ref={revealRef}>
						<h2 className="c-news__heading c-heading l-small-bottom-space">
							<SpecialText text={block.heading || "News"} />
						</h2>

						{/* Filter Menu */}

						<div className="c-news__filters l-bottom-section-space">
							{/* Category Filter */}
							{block.category_menu && block.category_menu.length > 0 && (
								<div className="c-news__filter-group c-tags">
									<button
										className={`c-news__filter-btn c-tags__tag ${selectedLocation === 'all' && selectedCategory === 'all' ? 'is-active' : ''}`}
										onClick={() => handleFilterChange('all', 'all')}
									>
										All
									</button>
									{block.category_menu.map((category, index) => (
										<button
											key={index}
											className={`c-news__filter-btn c-tags__tag ${selectedCategory === category.slug ? 'is-active' : ''}`}
											onClick={() => handleFilterChange('category', category.slug)}
										>
											{category.text}
										</button>
									))}
								</div>
							)}

							{/* Location Filter */}
							{block.location_menu && block.location_menu.length > 0 && (
								<div className="c-news__filter-group c-tags">
									{block.location_menu.map((location, index) => (
										<button
											key={index}
											className={`c-news__filter-btn c-tags__tag ${selectedLocation === location.slug ? 'is-active' : ''}`}
											onClick={() => handleFilterChange('location', location.slug)}
										>
											{location.text}
										</button>
									))}
								</div>
							)}
						</div>
					</div>
				</div>

				{/* News Grid with Quotes */}
				<div className="l-grid">
					<div className="l-grid__col">
						<div className="c-news-grid c-news-grid--filterable c-news-grid--news">
							{renderPostsWithQuotes()}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
